import {
  hasPermission,
  okSchema,
  SOFTWARE_FAMILY_INFO,
  softwareContextSchema,
  softwareJobSchema,
  type SoftwareContext,
  type SoftwareFamily,
  type SoftwareInstall,
  type SoftwareJob,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  CircleOff,
  Loader2,
  Play,
  Search,
  Square,
  Undo2,
  Unplug,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { EmptyState } from '@/components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import { serverQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';
import {
  softwareBuildsQuery,
  softwareContextQuery,
  softwarePreviewQuery,
  softwareUrl,
  softwareVersionsQuery,
} from '@/lib/software-queries';
import { canSwitch, switchBody } from '@/lib/software';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/server/$server/software')({ component: SoftwareTab });

/** "Paper 1.21.4 · build 232", or what is known of software the egg installed. */
function describe(s: SoftwareInstall) {
  const name = s.family ? SOFTWARE_FAMILY_INFO[s.family].label : 'Unknown software';
  const parts = [name, s.minecraftVersion ? `Minecraft ${s.minecraftVersion}` : null, s.build];
  return parts.filter(Boolean).join(' · ');
}

function SoftwareTab() {
  const { server: id } = Route.useParams();
  const context = useQuery(softwareContextQuery(id));

  if (context.isPending)
    return (
      <div className="grid gap-3" aria-busy>
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  if (context.isError)
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t load the server software</AlertTitle>
        <AlertDescription>{errorMessage(context.error)}</AlertDescription>
      </Alert>
    );
  const ctx = context.data;
  if (!ctx.supported)
    return (
      <EmptyState icon={CircleOff} title="Software can’t be switched on this server">
        Hearth switches Minecraft: Java Edition servers whose egg starts a jar named by a startup
        variable. This server’s game or egg doesn’t.
      </EmptyState>
    );
  return (
    <div className="grid gap-4">
      <CurrentCard server={id} ctx={ctx} />
      <JobBanner server={id} job={ctx.job} />
      <Picker server={id} ctx={ctx} />
    </div>
  );
}

function useCan(server: string) {
  const { data: s } = useSuspenseQuery(serverQuery(server));
  return (p: Parameters<typeof hasPermission>[1]) => hasPermission(s.permissions, p);
}

/** The server's state from the live socket, falling back to what the API saw. */
function useOffline(server: string, ctx: SoftwareContext) {
  const live = useServerLive(server);
  const state = live.state ?? ctx.serverState;
  return { state, offline: state === 'offline' };
}

function CurrentCard({ server, ctx }: { server: string; ctx: SoftwareContext }) {
  const can = useCan(server);
  const qc = useQueryClient();
  const { offline } = useOffline(server, ctx);
  const running = ctx.job?.status === 'running';
  const undo = useMutation({
    mutationFn: () =>
      apiRequest(`${softwareUrl(server)}/undo`, { method: 'POST', schema: softwareContextSchema }),
    onSuccess: (data) => {
      qc.setQueryData(softwareContextQuery(server).queryKey, data);
      void qc.invalidateQueries({ queryKey: ['server', server] });
      toast.success('Previous software restored. Start the server to use it.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const current = ctx.current;
  return (
    <Card className="flex-row flex-wrap items-start gap-4 p-4">
      <div className="bg-game-soft text-game grid size-10 shrink-0 place-items-center rounded-lg">
        <Boxes className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Installed now
        </p>
        <p className="text-lg font-semibold">{current ? describe(current) : 'Unknown'}</p>
        <p className="text-muted-foreground text-sm">
          {current?.managed
            ? `Installed by Hearth ${current.installedAt ? timeAgo(current.installedAt) : ''}.`
            : 'Installed by the egg. Hearth hasn’t switched this server yet.'}{' '}
          {current?.launch.argsFile
            ? 'Starts from unix_args.txt.'
            : current?.launch.jar
              ? `Starts ${current.launch.jar}.`
              : ''}{' '}
          Java image: <code className="text-foreground">{ctx.image}</code>
        </p>
      </div>
      {!ctx.previous && ctx.job?.wipe && ctx.job.status === 'succeeded' && (
        <p className="text-muted-foreground max-w-xs text-sm">
          Undo isn’t available: the last switch wiped the server, so there is nothing to go back to.
          {ctx.job.backup ? ` The backup “${ctx.job.backup.name}” has the old files.` : ''}
        </p>
      )}
      {ctx.previous && can('software.change') && (
        <Confirm
          title="Go back to the previous software?"
          description={`This puts ${describe(ctx.previous)} back, with the startup variables it had, and keeps what’s installed now so you can switch again. The server has to be stopped.`}
          action="Undo switch"
          onConfirm={() => undo.mutate()}
        >
          <Button variant="outline" disabled={!offline || running || undo.isPending}>
            {undo.isPending ? <Loader2 className="animate-spin" /> : <Undo2 />} Undo: back to{' '}
            {ctx.previous.family ? SOFTWARE_FAMILY_INFO[ctx.previous.family].label : 'previous'}
          </Button>
        </Confirm>
      )}
    </Card>
  );
}

function JobBanner({ server, job }: { server: string; job: SoftwareJob | null }) {
  const can = useCan(server);
  const live = useServerLive(server);
  const start = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/power`, {
        method: 'POST',
        body: { action: 'start' },
        schema: okSchema,
      }),
    onSuccess: () => toast.success('Starting…'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!job) return null;
  const what = job.family
    ? `${SOFTWARE_FAMILY_INFO[job.family].label} ${job.build ?? ''} for Minecraft ${job.minecraftVersion}`
    : 'the previous software';
  if (job.status === 'running' || job.status === 'queued')
    return (
      <Alert>
        <Loader2 className="animate-spin" />
        <AlertTitle>
          {job.step === 'backup' ? `Backing up before wiping for ${what}` : `Installing ${what}`}
        </AlertTitle>
        <AlertDescription>
          {job.step === 'backup'
            ? 'Nothing is wiped until the backup has finished. If it fails, the switch stops. '
            : job.wipe
              ? 'The server folder is wiped once the downloads are verified. '
              : ''}
          hearthd is downloading and, for mod loaders, running the official installer. This can take
          a few minutes; the server can’t be started until it finishes. Progress also shows in the
          console.
        </AlertDescription>
      </Alert>
    );
  if (job.status === 'failed')
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Couldn’t install {what}</AlertTitle>
        <AlertDescription className="grid gap-2">
          <p>
            {job.error?.message}
            {['wiped_install_failed', 'backup_failed'].includes(job.error?.code ?? '') ||
            (job.wipe && job.error?.code === 'interrupted')
              ? ''
              : ' Nothing was changed: the previous software is still in place.'}
          </p>
          {job.error?.log.length ? (
            <details>
              <summary className="cursor-pointer text-sm">Installer output</summary>
              <pre className="bg-muted mt-2 max-h-64 overflow-auto rounded-md p-2 text-xs whitespace-pre-wrap">
                {job.error.log.join('\n')}
              </pre>
            </details>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  // Succeeded: recent ones only, and only until the server has been started.
  const recent = job.finishedAt && Date.now() - Date.parse(job.finishedAt) < 6 * 3600_000;
  const offline = (live.state ?? 'offline') === 'offline';
  if (!recent || !offline) return null;
  return (
    <Alert>
      <CheckCircle2 />
      <AlertTitle>
        {job.kind === 'undo' ? 'Previous software restored' : `Installed ${what}`}
      </AlertTitle>
      <AlertDescription className="grid gap-3">
        <p>
          It takes effect when the server starts. Hearth doesn’t start it for you. On first start
          the game may ask you to accept Mojang’s EULA again.
        </p>
        {can('control.start') && (
          <div>
            <Button size="sm" onClick={() => start.mutate()} disabled={start.isPending}>
              {start.isPending ? <Loader2 className="animate-spin" /> : <Play />} Start server
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}

function Picker({ server, ctx }: { server: string; ctx: SoftwareContext }) {
  const [family, setFamily] = useState<SoftwareFamily>(
    ctx.current?.family && ctx.families.some((f) => f.family === ctx.current?.family)
      ? ctx.current.family
      : (ctx.families[0]?.family ?? 'paper'),
  );
  const [version, setVersion] = useState<string | null>(null);
  const [build, setBuild] = useState<string | null>(null);
  const pick = (f: SoftwareFamily) => {
    setFamily(f);
    setVersion(null);
    setBuild(null);
  };
  return (
    <Card className="gap-4 p-4">
      <div>
        <h2 className="font-semibold">Switch software</h2>
        <p className="text-muted-foreground text-sm">
          Pick the software, the Minecraft version and a build. Versions come from each project’s
          own API; hearthd checks every download against its published checksum.
        </p>
      </div>
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        role="radiogroup"
        aria-label="Software"
      >
        {ctx.families.map((f) => (
          <button
            key={f.family}
            type="button"
            role="radio"
            aria-checked={family === f.family}
            onClick={() => pick(f.family)}
            className={cn(
              'hover:bg-accent rounded-lg border p-3 text-left transition-colors',
              family === f.family && 'border-game bg-game-soft',
            )}
          >
            <span className="flex items-center gap-2 font-medium">
              {f.label}
              {ctx.current?.family === f.family && <Badge variant="secondary">Current</Badge>}
            </span>
            <span className="text-muted-foreground mt-1 block text-xs">{f.description}</span>
          </button>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <VersionList
          server={server}
          family={family}
          current={ctx.current?.minecraftVersion ?? null}
          value={version}
          onChange={(v) => {
            setVersion(v);
            setBuild(null);
          }}
        />
        {version ? (
          <BuildList
            server={server}
            family={family}
            version={version}
            current={
              ctx.current?.family === family && ctx.current.minecraftVersion === version
                ? ctx.current.build
                : null
            }
            value={build}
            onChange={setBuild}
          />
        ) : (
          <div className="text-muted-foreground grid place-items-center rounded-lg border border-dashed p-6 text-sm">
            Pick a Minecraft version to see its builds.
          </div>
        )}
      </div>
      {version && build && (
        <SwitchButton
          server={server}
          ctx={ctx}
          target={{ family, minecraftVersion: version, build }}
        />
      )}
    </Card>
  );
}

function VersionList({
  server,
  family,
  current,
  value,
  onChange,
}: {
  server: string;
  family: SoftwareFamily;
  current: string | null;
  value: string | null;
  onChange: (v: string) => void;
}) {
  const q = useQuery(softwareVersionsQuery(server, family));
  const [search, setSearch] = useState('');
  const [unstable, setUnstable] = useState(false);
  const shown = useMemo(
    () =>
      (q.data?.versions ?? []).filter(
        (v) =>
          (unstable || v.type === 'release' || v.id === value) &&
          (!search || v.id.toLowerCase().includes(search.trim().toLowerCase())),
      ),
    [q.data, unstable, search, value],
  );
  return (
    <div className="grid content-start gap-2">
      <Label htmlFor="software-version-search">Minecraft version</Label>
      <div className="relative">
        <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
        <Input
          id="software-version-search"
          className="pl-8"
          placeholder="Search versions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={unstable} onCheckedChange={(c) => setUnstable(c === true)} />
        Show snapshots and pre-releases
      </label>
      {q.isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : q.isError ? (
        <p className="text-destructive text-sm">{errorMessage(q.error)}</p>
      ) : (
        <ul className="max-h-72 overflow-auto rounded-lg border" aria-label="Minecraft versions">
          {shown.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                disabled={Boolean(v.unavailable)}
                title={v.unavailable ?? undefined}
                onClick={() => onChange(v.id)}
                aria-pressed={value === v.id}
                className={cn(
                  'hover:bg-accent flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50',
                  value === v.id && 'bg-game-soft',
                )}
              >
                <span className="font-medium">{v.id}</span>
                {v.id === q.data.latest && <Badge variant="success">Latest</Badge>}
                {v.id === current && <Badge variant="secondary">Installed</Badge>}
                {v.type !== 'release' && <Badge variant="warning">{v.type}</Badge>}
                {v.unavailable && (
                  <span className="text-muted-foreground ml-auto text-xs">Needs Forge egg</span>
                )}
              </button>
            </li>
          ))}
          {!shown.length && (
            <li className="text-muted-foreground px-3 py-6 text-center text-sm">
              No versions match.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

const CHANNEL: Record<string, { label: string; variant: 'success' | 'secondary' | 'warning' }> = {
  recommended: { label: 'Recommended', variant: 'success' },
  stable: { label: 'Stable', variant: 'secondary' },
  beta: { label: 'Beta', variant: 'warning' },
  experimental: { label: 'Experimental', variant: 'warning' },
};

function BuildList({
  server,
  family,
  version,
  current,
  value,
  onChange,
}: {
  server: string;
  family: SoftwareFamily;
  version: string;
  current: string | null;
  value: string | null;
  onChange: (v: string) => void;
}) {
  const q = useQuery(softwareBuildsQuery(server, family, version));
  const builds = q.data?.builds ?? [];
  // Pre-select the recommended build once the list arrives.
  const recommended = q.data?.recommended ?? null;
  useEffect(() => {
    if (value === null && recommended) onChange(recommended);
  }, [value, recommended, onChange]);
  return (
    <div className="grid content-start gap-2">
      <Label>Build</Label>
      {q.isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : q.isError ? (
        <p className="text-destructive text-sm">{errorMessage(q.error)}</p>
      ) : (
        <ul className="max-h-[22rem] overflow-auto rounded-lg border" aria-label="Builds">
          {builds.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                disabled={Boolean(b.unavailable)}
                title={b.unavailable ?? undefined}
                onClick={() => onChange(b.id)}
                aria-pressed={value === b.id}
                className={cn(
                  'hover:bg-accent flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm disabled:opacity-50',
                  value === b.id && 'bg-game-soft',
                )}
              >
                <span className="font-medium">{b.label}</span>
                {b.id === q.data.recommended && b.channel !== 'recommended' && (
                  <Badge variant="success">Suggested</Badge>
                )}
                <Badge variant={CHANNEL[b.channel]!.variant}>{CHANNEL[b.channel]!.label}</Badge>
                {b.id === q.data.latest && <Badge variant="outline">Latest</Badge>}
                {b.id === current && <Badge variant="secondary">Installed</Badge>}
                {b.publishedAt && (
                  <span className="text-muted-foreground ml-auto text-xs">
                    {timeAgo(b.publishedAt)}
                  </span>
                )}
              </button>
            </li>
          ))}
          {!builds.length && (
            <li className="text-muted-foreground px-3 py-6 text-center text-sm">No builds.</li>
          )}
        </ul>
      )}
    </div>
  );
}

function SwitchButton({
  server,
  ctx,
  target,
}: {
  server: string;
  ctx: SoftwareContext;
  target: { family: SoftwareFamily; minecraftVersion: string; build: string };
}) {
  const can = useCan(server);
  const [open, setOpen] = useState(false);
  const { offline, state } = useOffline(server, ctx);
  const stop = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/power`, {
        method: 'POST',
        body: { action: 'stop' },
        schema: okSchema,
      }),
    onSuccess: () => toast.success('Stopping…'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const busy = ctx.job?.status === 'running';
  if (!can('software.change'))
    return (
      <p className="text-muted-foreground text-sm">
        You can look, but switching software needs the “Switch the server software and version”
        permission.
      </p>
    );
  return (
    <div className="flex flex-wrap items-center gap-3 border-t pt-4">
      <Button onClick={() => setOpen(true)} disabled={!offline || busy}>
        <Boxes /> Review switch to {SOFTWARE_FAMILY_INFO[target.family].label}{' '}
        {target.minecraftVersion}
      </Button>
      {!offline && (
        <span className="text-muted-foreground flex items-center gap-2 text-sm">
          The server is {state}. Stop it first: software is only changed while it’s offline.
          {can('control.stop') && state !== 'stopping' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => stop.mutate()}
              disabled={stop.isPending}
            >
              <Square /> Stop server
            </Button>
          )}
        </span>
      )}
      {open && (
        <ReviewDialog server={server} ctx={ctx} target={target} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

function ReviewDialog({
  server,
  ctx,
  target,
  onClose,
}: {
  server: string;
  ctx: SoftwareContext;
  target: { family: SoftwareFamily; minecraftVersion: string; build: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [image, setImage] = useState<string | undefined>(undefined);
  const [accepted, setAccepted] = useState(false);
  const [wipe, setWipe] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  // A backup before a wipe is on by default whenever one can be taken.
  const [backup, setBackup] = useState(ctx.canBackup);
  const preview = useQuery(softwarePreviewQuery(server, { ...target, image }));
  const p = preview.data;
  // Offer the egg's matching image when the current one is too old.
  const suggested = p && !p.javaOk && ctx.canChangeImage ? p.suggestedImage : null;
  useEffect(() => {
    if (suggested && image === undefined) setImage(suggested);
  }, [suggested, image]);
  const start = useMutation({
    mutationFn: () =>
      apiRequest(`${softwareUrl(server)}/switch`, {
        method: 'POST',
        body: switchBody({
          target,
          image,
          accepted,
          wipe,
          confirmName,
          backup,
          canBackup: ctx.canBackup,
        }),
        schema: softwareJobSchema,
      }),
    onSuccess: (job) => {
      qc.setQueryData(softwareContextQuery(server).queryKey, (old) =>
        old ? { ...old, job } : old,
      );
      void qc.invalidateQueries({ queryKey: softwareContextQuery(server).queryKey });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const label = `${SOFTWARE_FAMILY_INFO[target.family].label} ${p?.buildLabel ?? target.build} for Minecraft ${target.minecraftVersion}`;
  const blocked = !canSwitch(p, { accepted, wipe, confirmName }, ctx.serverName);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Switch to {label}?</DialogTitle>
          <DialogDescription>
            {wipe
              ? 'hearthd deletes everything in the server’s folder, then installs this one and updates the startup command. The server starts fresh.'
              : 'hearthd sets the current software aside (Undo brings it back), installs this one and updates the startup command. Worlds, plugins, mods and settings stay where they are.'}
          </DialogDescription>
        </DialogHeader>
        {preview.isPending ? (
          <Skeleton className="h-32 rounded-lg" />
        ) : preview.isError ? (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>This build can’t be installed here</AlertTitle>
            <AlertDescription>{errorMessage(preview.error)}</AlertDescription>
          </Alert>
        ) : (
          <div className="grid gap-3 text-sm">
            {p!.warnings.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>Before you switch</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc space-y-1 pl-4">
                    {p!.warnings.map((w) => (
                      <li key={w.code}>{w.message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            {p!.javaRequired !== null && (
              <div className={cn('rounded-lg border p-3', !p!.javaOk && 'border-destructive')}>
                <p>
                  Needs Java {p!.javaRequired}.{' '}
                  {p!.javaOk
                    ? 'The image has it.'
                    : ctx.canChangeImage && p!.suggestedImage
                      ? 'The server’s image is older.'
                      : 'The server’s image is older, and you can’t change the image.'}
                </p>
                {ctx.canChangeImage && (
                  <label className="mt-2 grid gap-1">
                    <span className="text-muted-foreground text-xs">Docker image</span>
                    <select
                      className="bg-background rounded-md border px-2 py-1.5"
                      value={image ?? ctx.image}
                      onChange={(e) =>
                        setImage(e.target.value === ctx.image ? undefined : e.target.value)
                      }
                    >
                      {ctx.images.map((i) => (
                        <option key={i.image} value={i.image}>
                          {i.label} ({i.image})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}
            {p!.variables.length > 0 && (
              <div>
                <p className="font-medium">Startup variables</p>
                <ul className="text-muted-foreground">
                  {p!.variables.map((v) => (
                    <li key={v.name}>
                      <code>{v.name}</code>: {v.from || '(empty)'} →{' '}
                      <span className="text-foreground">{v.to}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p!.launch === 'args_file' && (
              <p className="text-muted-foreground">
                This version starts from <code>unix_args.txt</code>, which your egg’s startup uses
                when it exists.
              </p>
            )}
            {ctx.canWipe && (
              <div className={cn('grid gap-2 rounded-lg border p-3', wipe && 'border-destructive')}>
                <label className="flex items-start gap-2">
                  <Checkbox checked={wipe} onCheckedChange={(c) => setWipe(c === true)} />
                  <span>
                    <span className="font-medium">Wipe the server first</span>
                    <span className="text-muted-foreground block">
                      Deletes every file: worlds, plugins and mods, configs and eula.txt. Undo can’t
                      bring them back.
                    </span>
                  </span>
                </label>
                {wipe && (
                  <>
                    {ctx.canBackup ? (
                      <label className="flex items-start gap-2">
                        <Checkbox checked={backup} onCheckedChange={(c) => setBackup(c === true)} />
                        <span>
                          Back up first. The switch waits for the backup and stops, wiping nothing,
                          if it fails.
                        </span>
                      </label>
                    ) : (
                      <p className="text-muted-foreground">
                        No backup can be taken first (no free backup slot, or no permission). Make
                        one on the Backups tab if you want to keep anything.
                      </p>
                    )}
                    <label className="grid gap-1">
                      <span>
                        Type <strong>{ctx.serverName}</strong> to confirm
                      </span>
                      <Input
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        autoComplete="off"
                        aria-invalid={confirmName !== '' && confirmName !== ctx.serverName}
                      />
                    </label>
                  </>
                )}
              </div>
            )}
            {p!.warnings.length > 0 && (
              <label className="flex items-start gap-2">
                <Checkbox checked={accepted} onCheckedChange={(c) => setAccepted(c === true)} />
                <span>I understand. I have a backup or don’t need one.</span>
              </label>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => start.mutate()} disabled={blocked || start.isPending}>
            {start.isPending ? <Loader2 className="animate-spin" /> : <Boxes />}{' '}
            {wipe ? 'Wipe and switch' : 'Switch software'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
